// The Offer tab's record dialogs (lane B8, S12; CR-032, CR-033, CR-036, CR-034).
//  - RecordDialog: Extend verbal offer · Send written · Counter · Accept — each a dated fact, the verbal and counter ones
//    with a note tagged Internal ("Candidate-facing status contains no internal notes.").
//  - DeclineDialog (DC-203): the Decline reason (a published list; a reason that needs text — his *Other* — shows the
//    note field) and the Next path as four tiles, Alternate to offer · Continue · Reopen · Close; Alternate shows the
//    active alternates in rank order, Close shows the reporting category and final notes. The confirm stays dashed until
//    the reason and the path (and the path's own fields) are set: "Decline cannot close without reason and next path."
//  - NextPathDialog: the same four paths after a withdrawal or a reversal (offer.choose_next_path), with a rationale.
// Every dialog is a view over its own draft: the caller sends the input and keeps the dialog open on a refusal.

import { useState } from "react";
import {
  ACTION_LABELS, COMMON_LABELS, DEBRIEF_OUTCOME_LABELS, Dialog, Field, G1_LABELS, HISTORY_LABELS, Input, OFFER_STATUS, RadioTile,
  RankedList, RECO_LABELS, Select, SURFACE_LABELS, Tag, TAG_LABELS, TextArea, type Blocked, type RadioTileOption, type RankedItem,
} from "@/mc";
import { OFFER_RULE_LABELS, OFFER_TAB_LABELS } from "./labels";
import { NEXT_PATHS, type NextPath, type OfferAlternate, type ReasonOption } from "./types";

/** What every dialog shares: open state, the in-flight flag, the server's refusal, and the static (test) rendering. */
export interface DialogShell {
  open: boolean;
  onClose: () => void;
  busy?: boolean | undefined;
  refused?: string | null | undefined;
  preview?: boolean | undefined;
}

const PATH_WORDS: Readonly<Record<NextPath, string>> = {
  alternate: RECO_LABELS.alternateToOffer,
  continue: DEBRIEF_OUTCOME_LABELS.continue,
  reopen: DEBRIEF_OUTCOME_LABELS.reopen,
  close: RECO_LABELS.close,
};

const filled = (value: string | undefined | null): boolean => !!value && value.trim().length > 0;
const options = (list: readonly ReasonOption[] | null | undefined) => (list ?? []).map((item) => ({ value: item.value, label: item.label }));
const refusedOf = (refused: string | null | undefined) => (refused ? { reason: refused } : undefined);

/** The note a reason needs: a published `requires_note`, or the `other` slug (seed D8: his *Other*). */
export function needsNote(reasons: readonly ReasonOption[] | null | undefined, code: string): boolean {
  if (!code) return false;
  const found = (reasons ?? []).find((item) => item.value === code);
  return found?.requires_note === true || code === "other";
}

/** The active alternates in rank order (D8), as the ranked list draws them. */
export function rankedAlternates(alternates: readonly OfferAlternate[] | null | undefined): RankedItem[] {
  return [...(alternates ?? [])]
    .sort((a, b) => a.rank - b.rank)
    .map((alt) => ({ key: alt.application_id ?? alt.cand, name: alt.display_name ?? alt.cand, candidate: true, hueKey: alt.cand }));
}

// ---- the next path ---------------------------------------------------------------------------------------------------

export interface NextPathDraft {
  path: NextPath | "";
  category: string;
  finalNotes: string;
}

/** Why the next path is not yet complete, or undefined when it is. */
export function nextPathBlocked(draft: NextPathDraft, alternates: readonly OfferAlternate[] | null | undefined): Blocked | undefined {
  if (!draft.path) return { reason: OFFER_RULE_LABELS.nextPathRequired };
  if (draft.path === "alternate" && !(alternates ?? []).length) return { reason: OFFER_RULE_LABELS.nextPathRequired };
  if (draft.path === "close" && (!filled(draft.category) || !filled(draft.finalNotes))) return { reason: OFFER_RULE_LABELS.cancelRequires };
  return undefined;
}

export function NextPathFields({ draft, onDraft, alternates, categories }: {
  draft: NextPathDraft;
  onDraft: (draft: NextPathDraft) => void;
  alternates: readonly OfferAlternate[] | null | undefined;
  categories: readonly ReasonOption[] | null | undefined;
}) {
  const ranked = rankedAlternates(alternates);
  const tiles: RadioTileOption[] = NEXT_PATHS.map((path) => ({
    value: path,
    label: PATH_WORDS[path],
    // no active alternate: nobody to extend the offer to, so the tile is not drawn
    can: path === "alternate" ? ranked.length > 0 : true,
  }));
  return (
    <>
      <RadioTile label={OFFER_TAB_LABELS.nextPath} options={tiles} value={draft.path} row onChange={(value) => onDraft({ ...draft, path: value as NextPath })} />
      {draft.path === "alternate" ? <RankedList items={ranked} mode="view" /> : null}
      {draft.path === "close" ? (
        <>
          <Field label={G1_LABELS.reportingCategory} required>
            <Select options={options(categories)} value={draft.category} onChange={(value) => onDraft({ ...draft, category: value })} />
          </Field>
          <Field label={G1_LABELS.finalNotes} required>
            <TextArea rows={3} value={draft.finalNotes} onChange={(event) => onDraft({ ...draft, finalNotes: event.target.value })} />
          </Field>
        </>
      ) : null}
    </>
  );
}

/** The next path's tool fields (offer.record_decline and offer.choose_next_path share them). */
export function nextPathInput(draft: NextPathDraft, alternates: readonly OfferAlternate[] | null | undefined) {
  const first = rankedAlternates(alternates)[0];
  return {
    next_path: draft.path as NextPath,
    alternate_application_id: draft.path === "alternate" ? (first?.key ?? null) : null,
    final_notes: draft.path === "close" ? draft.finalNotes.trim() : null,
    reporting_category: draft.path === "close" ? draft.category : null,
  };
}

const EMPTY_PATH: NextPathDraft = { path: "", category: "", finalNotes: "" };

// ---- decline ------------------------------------------------------------------------------------------------------------

export interface DeclineDraft extends NextPathDraft {
  reason: string;
  note: string;
}

export interface DeclineInput {
  reason_code: string;
  note: string | null;
  next_path: NextPath;
  alternate_application_id: string | null;
  final_notes: string | null;
  reporting_category: string | null;
}

export function declineBlocked(draft: DeclineDraft, reasons: readonly ReasonOption[] | null | undefined, alternates: readonly OfferAlternate[] | null | undefined): Blocked | undefined {
  if (!filled(draft.reason)) return { reason: OFFER_RULE_LABELS.declineReasonRequired };
  if (needsNote(reasons, draft.reason) && !filled(draft.note)) return { reason: OFFER_RULE_LABELS.declineReasonRequired };
  return nextPathBlocked(draft, alternates);
}

export interface DeclineDialogProps extends DialogShell {
  reasons: readonly ReasonOption[] | null | undefined;
  categories: readonly ReasonOption[] | null | undefined;
  alternates: readonly OfferAlternate[] | null | undefined;
  initial?: Partial<DeclineDraft> | undefined;
  onSubmit: (input: DeclineInput) => void;
}

export function DeclineDialog({ open, onClose, busy, refused, preview, reasons, categories, alternates, initial, onSubmit }: DeclineDialogProps) {
  const [draft, setDraft] = useState<DeclineDraft>({ ...EMPTY_PATH, reason: "", note: "", ...initial });
  const blocked = declineBlocked(draft, reasons, alternates);
  const note = needsNote(reasons, draft.reason);
  const submit = () => {
    if (blocked) return;
    onSubmit({ reason_code: draft.reason, note: filled(draft.note) ? draft.note.trim() : null, ...nextPathInput(draft, alternates) });
  };
  return (
    <Dialog
      preset="recordDecline" open={open} onClose={onClose} preview={preview} dismissLabel={ACTION_LABELS.cancel}
      primary={{ label: ACTION_LABELS.decline, onClick: submit, blocked, busy, hs: "offer.confirm" }}
      refused={refusedOf(refused)} hint={G1_LABELS.reasonStored} width={620}
    >
      <div className="mc-offer-form">
        <Field label={G1_LABELS.declineReason} required>
          <Select options={options(reasons)} value={draft.reason}  onChange={(value) => setDraft({ ...draft, reason: value })} />
        </Field>
        {note ? (
          <Field label={SURFACE_LABELS.notes} required labelSlot={<Tag tone="internal">{TAG_LABELS.internal}</Tag>} hint={OFFER_RULE_LABELS.noInternalNotes}>
            <TextArea rows={3} value={draft.note}  onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
          </Field>
        ) : null}
        <NextPathFields draft={draft} onDraft={(next) => setDraft({ ...draft, ...next })} alternates={alternates} categories={categories} />
      </div>
    </Dialog>
  );
}

// ---- the next path after a withdrawal or a reversal ------------------------------------------------------------------

export interface NextPathInput {
  next_path: NextPath;
  alternate_application_id: string | null;
  rationale: string;
  final_notes: string | null;
  reporting_category: string | null;
}

export interface NextPathDialogProps extends DialogShell {
  categories: readonly ReasonOption[] | null | undefined;
  alternates: readonly OfferAlternate[] | null | undefined;
  initial?: Partial<NextPathDraft & { rationale: string }> | undefined;
  onSubmit: (input: NextPathInput) => void;
}

export function NextPathDialog({ open, onClose, busy, refused, preview, categories, alternates, initial, onSubmit }: NextPathDialogProps) {
  const [draft, setDraft] = useState<NextPathDraft & { rationale: string }>({ ...EMPTY_PATH, rationale: "", ...initial });
  // the rationale is 3–2000 characters (offer.choose_next_path)
  const blocked = nextPathBlocked(draft, alternates) ?? (draft.rationale.trim().length < 3 ? { reason: OFFER_RULE_LABELS.nextPathRequired } : undefined);
  const submit = () => {
    if (blocked) return;
    onSubmit({ ...nextPathInput(draft, alternates), rationale: draft.rationale.trim() });
  };
  return (
    <Dialog
      title={OFFER_TAB_LABELS.nextPath} open={open} onClose={onClose} preview={preview} dismissLabel={ACTION_LABELS.cancel}
      primary={{ label: ACTION_LABELS.save, onClick: submit, blocked, busy, hs: "offer.confirm" }}
      refused={refusedOf(refused)} hint={G1_LABELS.reasonStored} width={620}
    >
      <div className="mc-offer-form">
        <NextPathFields draft={draft} onDraft={(next) => setDraft({ ...draft, ...next })} alternates={alternates} categories={categories} />
        <Field label={G1_LABELS.rationale} required>
          <TextArea rows={3} maxLength={2000} value={draft.rationale} onChange={(event) => setDraft({ ...draft, rationale: event.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- the dated facts ------------------------------------------------------------------------------------------------------

export type RecordKind = "verbal" | "written" | "counter" | "accept";

export interface RecordDraft {
  date: string;
  text: string;
  note: string;
}

const RECORD_PRIMARY: Readonly<Record<RecordKind, string>> = {
  verbal: ACTION_LABELS.extendVerbalOffer,
  written: ACTION_LABELS.sendWritten,
  counter: ACTION_LABELS.counter,
  accept: ACTION_LABELS.accept,
};

const RECORD_DATE: Readonly<Record<RecordKind, string | null>> = {
  verbal: OFFER_STATUS.verbalExtended,
  written: OFFER_STATUS.writtenSent,
  counter: null,
  accept: OFFER_TAB_LABELS.acceptedDate,
};

/** What a record dialog still needs: the verbal terms (an internal note), the letter's reference, the counter's words,
 *  the accepted date. */
export function recordBlocked(kind: RecordKind, draft: RecordDraft): Blocked | undefined {
  const reason = { reason: COMMON_LABELS.required };
  if (kind === "verbal" && !filled(draft.note)) return reason;
  if (kind === "written" && !filled(draft.text)) return reason;
  if (kind === "counter" && !filled(draft.text)) return reason;
  if (kind === "accept" && !filled(draft.date)) return reason;
  return undefined;
}

export interface RecordDialogProps extends DialogShell {
  kind: RecordKind;
  initial?: Partial<RecordDraft> | undefined;
  onSubmit: (draft: RecordDraft) => void;
}

export function RecordDialog({ kind, open, onClose, busy, refused, preview, initial, onSubmit }: RecordDialogProps) {
  const [draft, setDraft] = useState<RecordDraft>({ date: "", text: "", note: "", ...initial });
  const blocked = recordBlocked(kind, draft);
  const dateLabel = RECORD_DATE[kind];
  const internal = <Tag tone="internal">{TAG_LABELS.internal}</Tag>;
  return (
    <Dialog
      title={RECORD_PRIMARY[kind]} open={open} onClose={onClose} preview={preview} dismissLabel={ACTION_LABELS.cancel}
      primary={{ label: RECORD_PRIMARY[kind], onClick: () => !blocked && onSubmit(draft), blocked, busy, hs: "offer.confirm" }}
      refused={refusedOf(refused)}
    >
      <div className="mc-offer-form">
        {dateLabel ? (
          <Field label={dateLabel} required={kind === "accept"}>
            <Input type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
          </Field>
        ) : null}
        {kind === "written" ? (
          <Field label={HISTORY_LABELS.evidence} required>
            <Input value={draft.text} maxLength={200} onChange={(event) => setDraft({ ...draft, text: event.target.value })} />
          </Field>
        ) : null}
        {kind === "counter" ? (
          <Field label={OFFER_TAB_LABELS.counterRequest} required>
            <TextArea rows={3} value={draft.text} onChange={(event) => setDraft({ ...draft, text: event.target.value })} />
          </Field>
        ) : null}
        {kind === "verbal" || kind === "counter" ? (
          <Field label={SURFACE_LABELS.notes} required={kind === "verbal"} labelSlot={internal} hint={OFFER_RULE_LABELS.noInternalNotes}>
            <TextArea rows={3} value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}
